import Check from '@mui/icons-material/Check'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { CustomVariable, CustomVariableValue } from '@/types/custom-variables'
import { Organization } from '@/types/organizations'
import ExcludedVendorsField from './ExcludedVendorsField'
import POInstructionsField from './POInstructionsField'

type DataProviderOrgPOCardProps = {
  dataProviderOrg: Organization
  performMatchVariable: CustomVariable
  instructionsVariable: CustomVariable
  excludedVendorsVariable: CustomVariable
  orgCredentialTypeId: string
  performMatchValue?: CustomVariableValue
  instructionsValue?: CustomVariableValue
  excludedVendorsValue?: CustomVariableValue
  onValueChange: (
    variableValue: Omit<CustomVariableValue, 'id' | 'project_id'> & {
      id?: string
    },
  ) => void
}

export default function DataProviderOrgPOCard({
  dataProviderOrg,
  performMatchVariable,
  instructionsVariable,
  excludedVendorsVariable,
  orgCredentialTypeId,
  performMatchValue,
  instructionsValue,
  excludedVendorsValue,
  onValueChange,
}: DataProviderOrgPOCardProps) {
  const isMatchOn = !performMatchValue || performMatchValue.value === 'true'

  // One row in the Companies card: the card draws the border and the lines
  // between companies, so the accordion itself stays flat.
  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ px: 2.5, minHeight: 64 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', pr: 1 }}
        >
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {dataProviderOrg.name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={isMatchOn ? 'success' : 'default'}
              icon={isMatchOn ? <Check /> : undefined}
              label={isMatchOn ? 'PO Match On' : 'PO Match Off'}
              variant={isMatchOn ? 'filled' : 'outlined'}
            />
            <Switch
              checked={isMatchOn}
              onClick={(e) => e.stopPropagation()}
              onChange={(_e, checked) => {
                onValueChange({
                  ...performMatchValue,
                  custom_variable_id: performMatchVariable.id,
                  owner_org_id: dataProviderOrg.id,
                  value: checked ? 'true' : 'false',
                })
              }}
              inputProps={{
                'aria-label': `Match purchase orders for ${dataProviderOrg.name}`,
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <Stack spacing={3}>
          <POInstructionsField
            label={`${dataProviderOrg.name} match instructions`}
            value={instructionsValue?.value ?? ''}
            onChange={(instructions) => {
              onValueChange({
                ...instructionsValue,
                custom_variable_id: instructionsVariable.id,
                owner_org_id: dataProviderOrg.id,
                value: instructions,
              })
            }}
            helperText={instructionsVariable.description || ''}
          />
          <ExcludedVendorsField
            orgId={dataProviderOrg.id}
            orgCredentialTypeId={orgCredentialTypeId}
            value={excludedVendorsValue?.value ?? ''}
            onChange={(excludedVendorNumbers) => {
              onValueChange({
                ...excludedVendorsValue,
                custom_variable_id: excludedVendorsVariable.id,
                owner_org_id: dataProviderOrg.id,
                value: excludedVendorNumbers,
              })
            }}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
